import React, { useMemo } from 'react';
import ReactSelect, { components, OptionsType, SelectComponentsConfig, StylesConfig, ValueType } from 'react-select';

import MIcon from '../MIcon/MIcon';
import MSelectValueContainer from './MSelectValueContainer/MSelectValueContainer';

import * as icons from '../../../assets/icons';

import classes from './MNewSelect.module.scss';
import { SelectOption } from './MNewSelect.model';
import { condClass } from 'app/utils/general';

interface Props<Value, IsMulti extends boolean = false, OptionType extends SelectOption<Value> = SelectOption<Value>> {
  className?: string;
  placeholder?: string;
  options: OptionsType<OptionType>;
  isMulti?: IsMulti;
  value?: ValueType<OptionType, IsMulti> | (IsMulti extends true ? OptionType['value'][] : OptionType['value']);
  customStylesSelect?: StylesConfig<OptionType, IsMulti>;
  filterOption?: ((option: SelectOption, inputValue: string) => boolean) | true;
  name?: string;
  valid?: boolean;
  invalid?: boolean;
  hideSelectedOptions?: boolean;
  showSelected?: boolean;
  disabled?: boolean;
  hideEffectSelection?: boolean;
  hideKeyboard?: boolean;
  onChange?: (value: ValueType<OptionType, IsMulti>) => void;
  readonly icon?: keyof typeof icons;
}

const MNewSelect = <Value, OptionType extends SelectOption<Value>, IsMulti extends boolean = false>(
  props: Props<Value, IsMulti, OptionType>,
): JSX.Element => {
  const componentsObj = useMemo(() => {
    const cmps: SelectComponentsConfig<OptionType, IsMulti> = { Menu: components.Menu };

    if (props.isMulti && !props.showSelected) {
      cmps.ValueContainer = MSelectValueContainer;
    }

    return cmps;
  }, [props.isMulti, props.showSelected]);

  const filterFunc = props.filterOption
    ? (option, inputValue) => option.label.toLowerCase().indexOf(inputValue) !== -1
    : props.filterOption;

  const value: ValueType<OptionType, IsMulti> = useMemo(() => {
    if (props.isMulti && Array.isArray(props.value)) {
      return props.options?.filter((item: OptionType) => (props.value as unknown as Value[]).includes(item.value)) as unknown as ValueType<
        OptionType,
        IsMulti
      >;
    }

    if (!props.isMulti && typeof props.value === 'string') {
      return props.options?.find((item: OptionType) => item.value === props.value) as unknown as ValueType<OptionType, IsMulti>;
    }

    // TODO: temporary for shuttle reservation - delete and fix there
    if (!props.isMulti && !!props.value && typeof props.value === 'object') {
      return props.options.find((item: OptionType) => JSON.stringify(item.value) === JSON.stringify(props.value)) as unknown as ValueType<OptionType, IsMulti>;
    }

    return props.value as ValueType<OptionType, IsMulti>;
  }, [props.value, props.isMulti, props.options]);

  const customStylesSelect: StylesConfig<OptionType, IsMulti> = useMemo(() => {
    return {
      control: (provided) => ({
        // none of react-select's styles are passed to <Control />
        ...provided,
        minHeight: '54px',
        borderRadius: '8px',
        paddingLeft: props.icon ? '40px' : '',
      }),
      input: (provided) => ({
        ...provided,
      }),
      container: (provided, state) => {
        return {
          ...provided,
          opacity: state.isDisabled ? 0.6 : 1,
        };
      },
      menu: (provided) => ({
        ...provided,
        zIndex: 2,
      }),
    };
  }, []);

  return (
    <div
      className={condClass({
        [classes['container']]: true,
        [String(props.className)]: !!props.className,
      })}
    >
      {props.icon ? <MIcon name={props.icon} className={classes['icon']}></MIcon> : null}
      <ReactSelect
        placeholder={props.placeholder || ''}
        name={props.name}
        styles={props.customStylesSelect ? props.customStylesSelect : customStylesSelect}
        isMulti={props.isMulti}
        value={value}
        closeMenuOnSelect={!props.isMulti}
        hideSelectedOptions={props.hideSelectedOptions}
        filterOption={filterFunc}
        components={componentsObj}
        onChange={props.onChange}
        options={props.options}
        isDisabled={props.disabled}
        isSearchable={!props.hideKeyboard}
      ></ReactSelect>
    </div>
  );
};

MNewSelect.defaultProps = {
  hideSelectedOptions: false,
};

export default MNewSelect;
